import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FlagOutlined as EndIcon,
  LocalShippingOutlined as TruckIcon,
  PersonOutlined as PersonIcon,
  PrintOutlined as PrintIcon,
  ScheduleOutlined as TimeIcon,
  TripOriginOutlined as StartIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import PageHeader from '../../components/PageHeader';
import RouteBuilderMap from '../../components/transportation/RouteBuilderMap';
import { Detail } from '../../components/monitoring/EvidenceSections';
import { APP_SHAPE } from '../../constants/shape';
import { getOpsRouteRun } from '../../services/route-run.service';
import { useOrganizationStore } from '../../stores/organization.store';
import type { OpsRouteRun } from '../../types/route-run.type';
import { formatKg, formatSar, formatWhen } from '../../utils/formatters';
import { STOP_STATUS_COLOR, facilityMarker, runTotals, stopsAsMapPoints } from './tripHelpers';

export default function TripDetails() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const [run, setRun] = useState<OpsRouteRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);
  const printReport = useReactToPrint({
    contentRef: reportRef,
    documentTitle: run ? `trip-${run.id}` : undefined,
    pageStyle: '@media print { .no-print { display: none !important; } .MuiPaper-root { box-shadow: none !important; } }',
  });

  const orgId = selectedOrganization?.id;
  const locale = i18n.resolvedLanguage || i18n.language;

  const load = useCallback(() => {
    if (!orgId || !runId) return;
    setLoading(true);
    setError(null);
    getOpsRouteRun(orgId, runId)
      .then(setRun)
      .catch(() => setError(t('trips.loadError')))
      .finally(() => setLoading(false));
  }, [orgId, runId, t]);

  useEffect(() => { load(); }, [load]);

  const mapPoints = useMemo(() => (run ? stopsAsMapPoints(run) : []), [run]);
  const totals = useMemo(() => (run ? runTotals(run) : null), [run]);

  if (!selectedOrganization) return <Alert severity="info">{t('transportation.routes.selectOrganization')}</Alert>;
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error || !run || !totals) {
    return <Alert severity="error" action={<Button color="inherit" onClick={load}>{t('suppliers.details.retry')}</Button>}>{error ?? t('trips.loadError')}</Alert>;
  }

  const start = run.route_snapshot?.start_facility;
  const end = run.route_snapshot?.end_facility;

  return (
    <Box>
      <PageHeader
        title={`${run.truck_name ?? run.truck_id} · ${run.route_name ?? '—'}`}
        subtitle={formatWhen(run.run_date, locale, 'date')}
        onBack={() => navigate('/monitoring/trips')}
        backLabel={t('transportation.routes.details.backLabel')}
        sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
        actions={(
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => printReport()}>
            {t('tripDetails.exportReport')}
          </Button>
        )}
      />

      {/* Everything inside reportRef is what the PDF/print carries: summary, route map, stops. */}
      <Box ref={reportRef}>
        <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 2, borderRadius: `${APP_SHAPE.radiusLg}px` }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ flex: 1 }}>{t('tripDetails.reportTitle')}</Typography>
            <Chip size="small" label={t(`trips.runStatus.${run.status}`, run.status)} />
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2.5 }}>
            <Detail icon={<TruckIcon />} label={t('orderDetails.truck')}>{run.truck_name ?? run.truck_id}</Detail>
            <Detail icon={<PersonIcon />} label={t('orderDetails.driver')}>{run.driver_name ?? t('trips.noDriver')}</Detail>
            <Detail icon={<TimeIcon />} label={t('tripDetails.clockWindow')}>
              {formatWhen(run.clock_in_at, locale, 'time')} → {formatWhen(run.clock_out_at, locale, 'time')}
            </Detail>
            <Detail icon={<TimeIcon />} label={t('trips.offloaded')}>{formatWhen(run.offload_at, locale, 'time')}</Detail>
            <Detail icon={<StartIcon />} label={t('transportation.routes.details.metrics.startFacility')}>{start?.name ?? '—'}</Detail>
            <Detail icon={<EndIcon />} label={t('transportation.routes.details.metrics.endFacility')}>{end?.name ?? '—'}</Detail>
            <Detail icon={<TruckIcon />} label={t('tripDetails.collectedStops')}>{totals.collected} / {run.stops.length}{totals.cancelled ? ` · ${t('trips.stopStatus.cancelled')}: ${totals.cancelled}` : ''}</Detail>
            <Detail icon={<TruckIcon />} label={t('tripDetails.totals')}>{formatKg(totals.weightKg, locale)} · {formatSar(totals.price, locale)}</Detail>
          </Box>
        </Paper>

        <Paper elevation={1} sx={{ mb: 2, borderRadius: `${APP_SHAPE.radiusLg}px`, overflow: 'hidden' }}>
          <Box sx={{ p: 2 }}><Typography variant="subtitle1" fontWeight={600}>{t('tripDetails.routeMap')}</Typography></Box>
          <Divider />
          <Box sx={{ height: 380 }}>
            <RouteBuilderMap
              selectedPoints={mapPoints}
              startFacility={facilityMarker(start)}
              endFacility={facilityMarker(end)}
              routedGeometry={(run.planned_geometry as any) ?? null}
              height="100%"
            />
          </Box>
        </Paper>

        <Paper elevation={1} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px`, overflow: 'hidden' }}>
          <Box sx={{ p: 2 }}><Typography variant="subtitle1" fontWeight={600}>{t('tripDetails.stops')}</Typography></Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={32}>#</TableCell>
                  <TableCell>{t('orderDetails.point')}</TableCell>
                  <TableCell>{t('collectionsMonitor.status')}</TableCell>
                  <TableCell align="right">{t('orderDetails.weight')}</TableCell>
                  <TableCell align="right">{t('orderDetails.price')}</TableCell>
                  <TableCell>{t('tripDetails.arrivedAt')}</TableCell>
                  <TableCell>{t('tripDetails.completedAt')}</TableCell>
                  <TableCell>{t('tripDetails.cancellation')}</TableCell>
                  <TableCell align="right" className="no-print">{t('suppliers.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {run.stops.map((stop, index) => (
                  <TableRow key={stop.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Typography variant="body2" fontWeight={600}>{stop.point_snapshot?.name ?? stop.collection_point_id}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[stop.point_snapshot?.supplier_name, stop.point_snapshot?.materials?.[0]?.name].filter(Boolean).join(' · ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Chip size="small" color={STOP_STATUS_COLOR[stop.status] ?? 'default'} label={t(`trips.stopStatus.${stop.status}`, stop.status)} />
                        {stop.order_approval_status ? (
                          <Tooltip title={stop.rejection_reason ?? ''}>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={stop.order_approval_status === 'approved' ? 'success' : stop.order_approval_status === 'rejected' ? 'error' : 'warning'}
                              label={t(`collectionsMonitor.approvalStatus.${stop.order_approval_status}`)}
                            />
                          </Tooltip>
                        ) : null}
                        {stop.handed_off ? (
                          <Chip size="small" variant="outlined" label={t('tripDetails.handedOff')} />
                        ) : null}
                        {stop.pickup_approval_status === 'approved' ? (
                          <Chip size="small" variant="outlined" color="success" label={t('orderApprovals.signed')} />
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{stop.handed_off ? '—' : formatKg(stop.weight_kg, locale)}</TableCell>
                    <TableCell align="right">{stop.handed_off ? '—' : formatSar(stop.price_amount, locale)}</TableCell>
                    <TableCell>{formatWhen(stop.arrived_at, locale, 'time')}</TableCell>
                    <TableCell>{formatWhen(stop.completed_at, locale, 'time')}</TableCell>
                    <TableCell>
                      {stop.status === 'cancelled' ? (
                        <Box>
                          <Typography variant="body2">{formatWhen(stop.cancelled_at, locale, 'time')}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {stop.cancellation_reason ? t(`tripDetails.cancelReason.${stop.cancellation_reason}`, stop.cancellation_reason) : ''}
                            {stop.cancellation_notes ? ` — ${stop.cancellation_notes}` : ''}
                          </Typography>
                        </Box>
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right" className="no-print">
                      {stop.pickup_order_id ? (
                        <Tooltip title={t('monitoring.orders.viewEvidence')}>
                          <IconButton size="small" onClick={() => navigate(`/orders/${stop.pickup_order_id}`)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
}
